import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, LogOut, Upload, ImageIcon, Check, X, Save } from "lucide-react";
import { api, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import { AdminSizeEditor } from "../components/AdminSizeEditor";
import { AdminPagesPanel } from "../components/AdminPagesPanel";

const MEDIUMS = ["charcoal", "pencil", "acrylic"];
const DEFAULT_SIZES = [
    { label: "A5", price: 0 },
    { label: "A4", price: 0 },
    { label: "A3", price: 0 },
    { label: "A2", price: 0 },
];

const emptyForm = () => ({
    title: "", medium: "charcoal", description: "", price: 100,
    is_explicit: false, is_published: true, is_original: false,
    sizes: DEFAULT_SIZES.map((s) => ({ ...s })),
    collection: "", release_year: new Date().getFullYear(),
    image_url: "", image_path: null,
});

export default function AdminDashboard() {
    const { admin, logout, loading } = useAuth();
    const { settings, refresh: refreshSettings } = useSettings();
    const [tab, setTab] = useState("prints");
    const [prints, setPrints] = useState([]);
    const [originals, setOriginals] = useState([]);
    const [commissions, setCommissions] = useState([]);
    const [orders, setOrders] = useState([]);
    const [waitlist, setWaitlist] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyForm());
    const [uploading, setUploading] = useState(false);
    const [siteForm, setSiteForm] = useState(settings);
    const [savingSite, setSavingSite] = useState(false);

    useEffect(() => { setSiteForm(settings); }, [settings]);

    const load = async () => {
        try {
            const [pR, oR2, cR, oR, wR] = await Promise.all([
                api.get("/admin/prints"),
                api.get("/admin/prints", { params: { original: true } }),
                api.get("/admin/commissions"),
                api.get("/admin/orders"),
                api.get("/admin/waitlist"),
            ]);
            setPrints(pR.data);
            setOriginals(oR2.data);
            setCommissions(cR.data);
            setOrders(oR.data);
            setWaitlist(wR.data);
        } catch (e) { /* ignore */ }
    };

    useEffect(() => { if (admin) load(); }, [admin]);

    if (loading) return null;
    if (!admin) return <Navigate to="/admin/login" replace />;

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post("/admin/prints/upload-image", fd);
            setForm((f) => ({ ...f, image_path: r.data.path, image_url: "" }));
            toast.success("Image uploaded");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upload failed");
        } finally { setUploading(false); }
    };

    const updateSize = (idx, key, val) => {
        setForm((f) => {
            const sizes = [...f.sizes];
            sizes[idx] = { ...sizes[idx], [key]: key === "price" ? parseFloat(val || 0) : val };
            return { ...f, sizes };
        });
    };

    const addSizeRow = () => setForm((f) => ({ ...f, sizes: [...f.sizes, { label: "", price: 0 }] }));
    const removeSizeRow = (idx) => setForm((f) => ({ ...f, sizes: f.sizes.filter((_, i) => i !== idx) }));

    const submitPrint = async (e) => {
        e.preventDefault();
        if (!form.image_url && !form.image_path) {
            toast.error("Please upload an image or provide a URL");
            return;
        }
        try {
            const cleanSizes = form.sizes.filter((s) => s.label.trim());
            await api.post("/admin/prints", {
                ...form,
                price: parseFloat(form.price),
                release_year: form.release_year ? parseInt(form.release_year) : null,
                collection: form.collection?.trim() || null,
                sizes: cleanSizes.map((s) => ({ label: s.label.trim(), price: parseFloat(s.price || 0) })),
            });
            toast.success(`${form.is_original ? "Original" : "Print"} added`);
            setForm(emptyForm());
            setShowForm(false);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Create failed");
        }
    };

    const deletePrint = async (id, isOriginal) => {
        if (!window.confirm(`Delete this ${isOriginal ? "original" : "print"}?`)) return;
        await api.delete(`/admin/prints/${id}`);
        load();
        toast.success("Deleted");
    };

    const togglePublished = async (item) => {
        try {
            await api.patch(`/admin/prints/${item.id}`, { is_published: !item.is_published });
            toast.success(item.is_published ? "Hidden from site" : "Now visible on site");
            load();
        } catch (e) {
            toast.error("Update failed");
        }
    };

    const toggleSold = async (item) => {
        try {
            await api.patch(`/admin/prints/${item.id}`, { is_sold: !item.is_sold });
            toast.success(item.is_sold ? "Marked as available" : "Marked as SOLD");
            load();
        } catch (e) {
            toast.error("Update failed");
        }
    };

    return (
        <div className="relative z-10 pt-28 pb-24 min-h-screen" data-testid="admin-dashboard">
            <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
                <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
                    <div>
                        <div className="label-caps text-[#8C3A3A] mb-2">Studio Admin</div>
                        <h1 className="font-serif-display text-4xl text-[#1A1A1A] font-light tracking-tighter">Dashboard</h1>
                    </div>
                    <button onClick={logout} className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#5A5A5A] hover:text-[#1A1A1A]" data-testid="admin-logout">
                        <LogOut className="w-3 h-3" /> Sign out
                    </button>
                </div>

                <div className="flex flex-wrap gap-2 border-b border-black/[0.06] mb-8">
                    {[
                        { k: "prints", label: `Prints (${prints.length})` },
                        { k: "originals", label: `Originals (${originals.length})` },
                        { k: "commissions", label: `Commissions (${commissions.length})` },
                        { k: "orders", label: `Orders (${orders.length})` },
                        { k: "waitlist", label: `Waitlist (${waitlist.length})` },
                        { k: "content", label: "Site Content" },
                        { k: "pages", label: "Pages" },
                    ].map((t) => (
                        <button
                            key={t.k}
                            onClick={() => setTab(t.k)}
                            data-testid={`admin-tab-${t.k}`}
                            className={`px-6 py-3 text-xs tracking-[0.2em] uppercase transition-colors ${tab === t.k ? "text-[#1A1A1A] border-b-2 border-[#8C3A3A]" : "text-[#5A5A5A] hover:text-[#1A1A1A]"}`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                {(tab === "prints" || tab === "originals") && (
                    <div>
                        <div className="mb-6 flex flex-wrap gap-3 items-center">
                            <button
                                onClick={() => {
                                    setShowForm((v) => !v);
                                    setForm({ ...emptyForm(), is_original: tab === "originals" });
                                }}
                                className="inline-flex items-center gap-2 bg-[#1A1A1A] text-[#FAFAF7] px-6 py-3 rounded-full text-xs tracking-[0.2em] uppercase font-medium hover:bg-[#8C3A3A] hover:text-white transition-colors"
                                data-testid="admin-add-print-btn"
                            >
                                <Plus className="w-3 h-3" /> {showForm ? "Cancel" : `Add ${tab === "originals" ? "Original" : "Print"}`}
                            </button>
                        </div>

                        {showForm && (
                            <form onSubmit={submitPrint} className="bg-[#F0EDE5] border border-black/[0.06] p-8 mb-10 grid md:grid-cols-2 gap-6" data-testid="admin-print-form">
                                <div className="md:col-span-2 flex items-center gap-2 pb-2 border-b border-black/[0.06]">
                                    <label className="flex items-center gap-2 text-sm text-[#5A5A5A]">
                                        <input type="checkbox" checked={form.is_original} onChange={(e) => setForm({ ...form, is_original: e.target.checked })} data-testid="admin-print-original" />
                                        <span className="label-caps text-[#1A1A1A]">One-of-One Original</span>
                                    </label>
                                    <span className="label-caps text-[#8A8A8A]">
                                        {form.is_original ? "· single price, no sizes" : "· printed in A4/A3/A2 sizes"}
                                    </span>
                                </div>

                                <div>
                                    <label className="label-caps block mb-2">Title</label>
                                    <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="underline-input" data-testid="admin-print-title" />
                                </div>
                                <div>
                                    <label className="label-caps block mb-2">{form.is_original ? "Price (USD)" : "Base price (fallback, USD)"}</label>
                                    <input required type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="underline-input" data-testid="admin-print-price" />
                                </div>
                                <div>
                                    <label className="label-caps block mb-2">Medium</label>
                                    <select value={form.medium} onChange={(e) => setForm({ ...form, medium: e.target.value })} className="underline-input appearance-none" data-testid="admin-print-medium">
                                        {MEDIUMS.map((m) => <option key={m} value={m}>{m}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="label-caps block mb-2">Release year</label>
                                    <input type="number" min="1990" max="2100" value={form.release_year} onChange={(e) => setForm({ ...form, release_year: e.target.value })} className="underline-input" data-testid="admin-print-year" />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="label-caps block mb-2">Series / Collection (optional)</label>
                                    <input placeholder="e.g. Charcoal Nudes, Amber Series" value={form.collection} onChange={(e) => setForm({ ...form, collection: e.target.value })} className="underline-input" data-testid="admin-print-collection" />
                                </div>
                                <div className="flex items-center gap-6 pt-6">
                                    <label className="flex items-center gap-2 text-sm text-[#5A5A5A]">
                                        <input type="checkbox" checked={form.is_explicit} onChange={(e) => setForm({ ...form, is_explicit: e.target.checked })} data-testid="admin-print-explicit" />
                                        <span className="label-caps">Explicit (blur)</span>
                                    </label>
                                    <label className="flex items-center gap-2 text-sm text-[#5A5A5A]">
                                        <input type="checkbox" checked={form.is_published} onChange={(e) => setForm({ ...form, is_published: e.target.checked })} data-testid="admin-print-published" />
                                        <span className="label-caps">Published</span>
                                    </label>
                                </div>
                                <div className="md:col-span-2">
                                    <label className="label-caps block mb-2">Description</label>
                                    <textarea rows="3" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="underline-input resize-none" data-testid="admin-print-description" />
                                </div>

                                {!form.is_original && (
                                    <div className="md:col-span-2">
                                        <label className="label-caps block mb-3">Size variants (leave price as 0 to hide a size)</label>
                                        <div className="space-y-2" data-testid="admin-sizes-editor">
                                            {form.sizes.map((s, idx) => (
                                                <div key={idx} className="grid grid-cols-[1fr_1fr_auto] gap-3 items-center">
                                                    <input placeholder="Label (e.g. A4)" value={s.label} onChange={(e) => updateSize(idx, "label", e.target.value)} className="underline-input" data-testid={`admin-size-label-${idx}`} />
                                                    <input type="number" step="0.01" placeholder="Price (USD)" value={s.price} onChange={(e) => updateSize(idx, "price", e.target.value)} className="underline-input" data-testid={`admin-size-price-${idx}`} />
                                                    <button type="button" onClick={() => removeSizeRow(idx)} className="text-[#8A8A8A] hover:text-[#8C3A3A] p-2" data-testid={`admin-size-remove-${idx}`}><X className="w-3 h-3" /></button>
                                                </div>
                                            ))}
                                            <button type="button" onClick={addSizeRow} className="mt-2 text-xs tracking-[0.2em] uppercase text-[#5A5A5A] hover:text-[#1A1A1A]" data-testid="admin-size-add">+ add size</button>
                                        </div>
                                    </div>
                                )}

                                <div className="md:col-span-2">
                                    <label className="label-caps block mb-3">Image</label>
                                    <div className="flex gap-4 items-start">
                                        <label className="flex-1 flex items-center gap-2 border border-dashed border-black/15 px-6 py-4 cursor-pointer hover:border-black/40 transition-colors">
                                            <Upload className="w-4 h-4 text-[#5A5A5A]" />
                                            <span className="text-sm text-[#5A5A5A]">{uploading ? "Uploading..." : form.image_path ? "Image uploaded ✓" : "Upload image"}</span>
                                            <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} data-testid="admin-print-image-upload" />
                                        </label>
                                        <div className="flex-1">
                                            <input placeholder="or paste image URL" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value, image_path: null })} className="underline-input" data-testid="admin-print-image-url" />
                                        </div>
                                    </div>
                                </div>

                                <div className="md:col-span-2">
                                    <button type="submit" className="bg-[#1A1A1A] text-[#FAFAF7] px-10 py-4 rounded-full text-sm tracking-wide font-medium hover:bg-[#8C3A3A] hover:text-white transition-colors" data-testid="admin-print-submit">
                                        Save
                                    </button>
                                </div>
                            </form>
                        )}

                        <div className="grid gap-3">
                            {(tab === "originals" ? originals : prints).map((p) => (
                                <div key={p.id} className={`flex flex-wrap items-center gap-4 p-4 bg-[#F0EDE5] border border-black/[0.06] ${p.is_published ? "" : "opacity-60"}`} data-testid={`admin-print-row-${p.id}`}>
                                    <div className="w-16 h-20 bg-[#FAFAF7] overflow-hidden shrink-0">
                                        {(p.image_url || p.image_path) ? (
                                            <img src={fileUrl(p.image_url || p.image_path)} alt="" className="w-full h-full object-cover" />
                                        ) : <ImageIcon className="w-full h-full p-4 text-[#8A8A8A]" />}
                                    </div>
                                    <div className="flex-1">
                                        <div className="text-[#1A1A1A] font-serif-display text-xl">
                                            {p.title}
                                            {p.is_sold && <span className="ml-3 label-caps text-[#8C3A3A]">· sold</span>}
                                            {!p.is_published && <span className="ml-3 label-caps text-[#8A8A8A]" data-testid={`admin-hidden-badge-${p.id}`}>· hidden</span>}
                                        </div>
                                        <div className="label-caps mt-1">
                                            {p.medium} · A${p.price}
                                            {p.release_year && ` · ${p.release_year}`}
                                            {p.collection && ` · ${p.collection}`}
                                            {p.is_explicit && <span className="text-[#8C3A3A]"> · explicit</span>}
                                            {!p.is_original && (p.sizes || []).length > 0 && (
                                                <span> · {(p.sizes || []).map((s) => `${s.label} ${s.price > 0 ? `A$${s.price}` : "on request"}`).join(", ")}</span>
                                            )}
                                        </div>
                                    </div>
                                    {p.is_original && (
                                        <button
                                            onClick={() => toggleSold(p)}
                                            className={`inline-flex items-center gap-2 px-4 py-2 border text-xs tracking-[0.2em] uppercase transition-colors ${
                                                p.is_sold
                                                    ? "bg-[#8C3A3A] text-white border-[#8C3A3A] hover:opacity-90"
                                                    : "border-black/20 text-[#1A1A1A] hover:bg-black/5"
                                            }`}
                                            data-testid={`admin-sold-toggle-${p.id}`}
                                        >
                                            {p.is_sold ? (<><X className="w-3 h-3" /> Sold — click to unmark</>) : (<><Check className="w-3 h-3" /> Mark Sold</>)}
                                        </button>
                                    )}
                                    <button
                                        onClick={() => setEditingId(editingId === p.id ? null : p.id)}
                                        className="px-4 py-2 border border-black/20 text-xs tracking-[0.2em] uppercase hover:bg-black/5 transition-colors"
                                        data-testid={`admin-edit-prices-${p.id}`}
                                    >
                                        {editingId === p.id ? "Close" : "Edit prices"}
                                    </button>
                                    <button
                                        onClick={() => togglePublished(p)}
                                        className="px-4 py-2 border border-black/20 text-xs tracking-[0.2em] uppercase hover:bg-black/5 transition-colors"
                                        data-testid={`admin-publish-toggle-${p.id}`}
                                    >
                                        {p.is_published ? "Hide" : "Show"}
                                    </button>
                                    <button onClick={() => deletePrint(p.id, p.is_original)} className="text-[#8A8A8A] hover:text-[#8C3A3A] p-2" data-testid={`admin-delete-${p.id}`} title="Delete">
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                    {editingId === p.id && (
                                        <AdminSizeEditor key={p.id} item={p} onSaved={() => { setEditingId(null); load(); }} />
                                    )}
                                </div>
                            ))}
                            {(tab === "originals" ? originals : prints).length === 0 && (
                                <div className="text-[#5A5A5A] label-caps py-12 text-center">Nothing here yet.</div>
                            )}
                        </div>
                    </div>
                )}

                {tab === "commissions" && (
                    <div className="grid gap-3">
                        {commissions.length === 0 && <div className="text-[#5A5A5A] label-caps py-12 text-center">No inquiries yet.</div>}
                        {commissions.map((c) => (
                            <div key={c.id} className="p-6 bg-[#F0EDE5] border border-black/[0.06]" data-testid={`admin-commission-${c.id}`}>
                                <div className="flex flex-wrap justify-between gap-4 mb-3">
                                    <div>
                                        <div className="text-[#1A1A1A] font-serif-display text-xl">{c.name}</div>
                                        <a href={`mailto:${c.email}`} className="text-sm text-[#5A5A5A] hover:text-[#1A1A1A]">{c.email}</a>
                                    </div>
                                    <div className="label-caps text-[#8C3A3A]">{c.status}</div>
                                </div>
                                <div className="grid sm:grid-cols-3 gap-4 mb-4 text-sm">
                                    <div><span className="label-caps block">Medium</span><span className="text-[#1A1A1A]">{c.medium}</span></div>
                                    <div><span className="label-caps block">Size</span><span className="text-[#1A1A1A]">{c.size}</span></div>
                                    <div><span className="label-caps block">Budget</span><span className="text-[#1A1A1A]">{c.budget || "—"}</span></div>
                                </div>
                                <p className="text-[#5A5A5A] text-sm leading-relaxed">{c.description}</p>
                                {c.reference_path && (
                                    <a href={fileUrl(c.reference_path)} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#5A5A5A] hover:text-[#1A1A1A]">
                                        View reference →
                                    </a>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {tab === "orders" && (
                    <div className="grid gap-3">
                        {orders.length === 0 && <div className="text-[#5A5A5A] label-caps py-12 text-center">No orders yet.</div>}
                        {orders.map((o) => (
                            <div key={o.session_id} className="p-4 bg-[#F0EDE5] border border-black/[0.06] flex flex-wrap items-center gap-4" data-testid={`admin-order-${o.session_id}`}>
                                <div className="flex-1 min-w-[200px]">
                                    <div className="text-[#1A1A1A] text-sm">{o.email || "guest"}</div>
                                    <div className="text-xs text-[#8A8A8A] mt-1">{o.session_id.slice(0, 20)}...</div>
                                </div>
                                <div className="label-caps">A${(o.amount / 100).toFixed(2)}</div>
                                <div className={`label-caps ${o.payment_status === "paid" ? "text-[#8C3A3A]" : "text-[#8A8A8A]"}`}>{o.payment_status}</div>
                            </div>
                        ))}
                    </div>
                )}

                {tab === "waitlist" && (
                    <div className="grid gap-2">
                        {waitlist.length === 0 && <div className="text-[#5A5A5A] label-caps py-12 text-center">No one on the studio list yet.</div>}
                        {waitlist.map((w) => (
                            <div key={w.id} className="p-4 bg-[#F0EDE5] border border-black/[0.06] flex flex-wrap items-center gap-4" data-testid={`admin-waitlist-${w.id}`}>
                                <div className="flex-1 min-w-[200px]">
                                    <a href={`mailto:${w.email}`} className="text-[#1A1A1A] text-sm hover:text-[#8C3A3A]">{w.email}</a>
                                    <div className="text-xs text-[#8A8A8A] mt-1">{new Date(w.created_at).toLocaleDateString()}</div>
                                </div>
                                <div className="label-caps text-[#5A5A5A]">{w.source || "general"}</div>
                            </div>
                        ))}
                    </div>
                )}

                {tab === "pages" && <AdminPagesPanel />}

                {tab === "content" && (
                    <form
                        onSubmit={async (e) => {
                            e.preventDefault();
                            setSavingSite(true);
                            try {
                                await api.put("/admin/settings", siteForm);
                                await refreshSettings();
                                toast.success("Site content updated");
                            } catch (err) {
                                toast.error("Save failed");
                            } finally {
                                setSavingSite(false);
                            }
                        }}
                        className="max-w-3xl space-y-10"
                        data-testid="admin-site-form"
                    >
                        <div>
                            <div className="label-caps text-[#8C3A3A] mb-4">Home Hero</div>
                            <div className="space-y-6 bg-[#F0EDE5] border border-black/[0.06] p-6">
                                <div>
                                    <label className="label-caps block mb-2">Kicker (small line above headline)</label>
                                    <input value={siteForm.hero_kicker || ""} onChange={(e) => setSiteForm({ ...siteForm, hero_kicker: e.target.value })} className="underline-input" data-testid="site-hero-kicker" />
                                </div>
                                <div>
                                    <label className="label-caps block mb-2">Headline</label>
                                    <input value={siteForm.hero_headline || ""} onChange={(e) => setSiteForm({ ...siteForm, hero_headline: e.target.value })} className="underline-input" data-testid="site-hero-headline" />
                                </div>
                                <div>
                                    <label className="label-caps block mb-2">Subline (below headline)</label>
                                    <textarea rows="2" value={siteForm.hero_subline || ""} onChange={(e) => setSiteForm({ ...siteForm, hero_subline: e.target.value })} className="underline-input resize-none" data-testid="site-hero-subline" />
                                </div>
                                <div>
                                    <label className="label-caps block mb-2">Description paragraph</label>
                                    <textarea rows="3" value={siteForm.hero_description || ""} onChange={(e) => setSiteForm({ ...siteForm, hero_description: e.target.value })} className="underline-input resize-none" data-testid="site-hero-description" />
                                </div>
                            </div>
                        </div>

                        <div>
                            <div className="label-caps text-[#8C3A3A] mb-4">About Page</div>
                            <div className="space-y-6 bg-[#F0EDE5] border border-black/[0.06] p-6">
                                <div className="grid sm:grid-cols-2 gap-6">
                                    <div>
                                        <label className="label-caps block mb-2">Title line 1</label>
                                        <input value={siteForm.about_title_line1 || ""} onChange={(e) => setSiteForm({ ...siteForm, about_title_line1: e.target.value })} className="underline-input" data-testid="site-about-title-1" />
                                    </div>
                                    <div>
                                        <label className="label-caps block mb-2">Title line 2 (italic)</label>
                                        <input value={siteForm.about_title_line2 || ""} onChange={(e) => setSiteForm({ ...siteForm, about_title_line2: e.target.value })} className="underline-input" data-testid="site-about-title-2" />
                                    </div>
                                </div>
                                <div>
                                    <label className="label-caps block mb-2">First paragraph</label>
                                    <textarea rows="5" value={siteForm.about_paragraph_1 || ""} onChange={(e) => setSiteForm({ ...siteForm, about_paragraph_1: e.target.value })} className="underline-input resize-none" data-testid="site-about-p1" />
                                </div>
                                <div>
                                    <label className="label-caps block mb-2">Second paragraph</label>
                                    <textarea rows="5" value={siteForm.about_paragraph_2 || ""} onChange={(e) => setSiteForm({ ...siteForm, about_paragraph_2: e.target.value })} className="underline-input resize-none" data-testid="site-about-p2" />
                                </div>
                            </div>
                        </div>

                        <div>
                            <div className="label-caps text-[#8C3A3A] mb-4">Contact & Social</div>
                            <div className="space-y-6 bg-[#F0EDE5] border border-black/[0.06] p-6">
                                <div className="grid sm:grid-cols-2 gap-6">
                                    <div>
                                        <label className="label-caps block mb-2">Email</label>
                                        <input type="email" value={siteForm.contact_email || ""} onChange={(e) => setSiteForm({ ...siteForm, contact_email: e.target.value })} className="underline-input" data-testid="site-contact-email" />
                                    </div>
                                    <div>
                                        <label className="label-caps block mb-2">Mobile</label>
                                        <input value={siteForm.contact_phone || ""} onChange={(e) => setSiteForm({ ...siteForm, contact_phone: e.target.value })} className="underline-input" data-testid="site-contact-phone" />
                                    </div>
                                    <div>
                                        <label className="label-caps block mb-2">Instagram — personal (no @)</label>
                                        <input value={siteForm.instagram_personal || ""} onChange={(e) => setSiteForm({ ...siteForm, instagram_personal: e.target.value })} className="underline-input" data-testid="site-ig-personal" />
                                    </div>
                                    <div>
                                        <label className="label-caps block mb-2">Instagram — portfolio (no @)</label>
                                        <input value={siteForm.instagram_studio || ""} onChange={(e) => setSiteForm({ ...siteForm, instagram_studio: e.target.value })} className="underline-input" data-testid="site-ig-studio" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={savingSite}
                            className="inline-flex items-center gap-2 bg-[#1A1A1A] text-[#FAFAF7] px-10 py-4 rounded-full text-sm tracking-wide font-medium hover:bg-[#8C3A3A] disabled:opacity-50 transition-colors"
                            data-testid="site-save-btn"
                        >
                            <Save className="w-4 h-4" /> {savingSite ? "Saving..." : "Save changes"}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
}
